/**
 * ECS service BFF contracts.
 *
 * Browser DTOs, ECS wire DTOs and ECS App DTOs deliberately live together so
 * the boundary cannot accidentally become a raw proxy. The browser schemas
 * are strict; only explicitly opaque provider configuration is passthrough.
 */

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

export function containsControlCharacters(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
}

export const OpaqueIdentifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .refine((value) => !containsControlCharacters(value), 'Must not contain control characters');

export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(254)
  .openapi({ example: 'customer@example.com' });

export const CountryCodeSchema = z
  .string()
  .trim()
  .length(2)
  .regex(/^[A-Za-z]{2}$/, 'Must be an ISO country code')
  .transform((value) => value.toUpperCase())
  .openapi({ example: 'US' });

/** ECS App's uppercase brand enum. The browser never supplies this value. */
export const EcsBrandSchema = z.enum(['BREITLING', 'GALLET', 'UG']);

export const LocaleSchema = z
  .string()
  .trim()
  .min(2)
  .max(32)
  .regex(/^[A-Za-z]{2,3}(?:[-_][A-Za-z]{2,4})?$/, 'Must be a storefront locale')
  .transform((value) => value.toLowerCase())
  .openapi({ example: 'us-en' });

export const PageQuerySchema = z.coerce.number().int().min(1).max(100).default(1);
export const OptionalPageQuerySchema = z.coerce.number().int().min(0).max(100).optional();
export const CountryCodeQuerySchema = z
  .object({ countryCode: CountryCodeSchema })
  .strict()
  .openapi('CountryCodeQuery');

export const ServiceAddressSchema = z
  .object({
    address1: z.string().trim().max(128).default(''),
    address2: z.string().trim().max(128).default(''),
    address3: z.string().trim().max(128).default(''),
    addressType: OpaqueIdentifierSchema,
    city: OpaqueIdentifierSchema,
    country: CountryCodeSchema,
    email: EmailSchema,
    firstName: OpaqueIdentifierSchema,
    lastName: OpaqueIdentifierSchema,
    mobileNumber: z.string().trim().max(40).default(''),
    phoneNumber: z.string().trim().max(40).default(''),
    state: z.string().trim().max(128).default(''),
    title: z.string().trim().max(32).nullable().optional(),
    zipCode: OpaqueIdentifierSchema,
  })
  .strict()
  .openapi('ServiceAddress');

export type ServiceAddress = z.infer<typeof ServiceAddressSchema>;

export const DeliveryOptionSchema = z.enum(['clientShipping', 'boutiquePickup']);
export const ModeOfTransportSchema = z.enum([
  'BreitlingSending',
  'ClientSending',
  'BoutiqueDropoff',
]);

export const RepairItemSchema = z
  .object({
    dateOfPurchase: z.string().trim().max(64).optional(),
    underWarranty: z.boolean().optional(),
    watchReference: OpaqueIdentifierSchema.optional(),
    watchSerial: OpaqueIdentifierSchema.optional(),
  })
  .strict()
  .openapi('RepairItemInput');

export const RepairReasonInputSchema = z
  .object({
    reasonCodes: z.array(OpaqueIdentifierSchema).min(1).max(20),
    remarks: z.string().trim().max(2000).optional(),
  })
  .strict()
  .openapi('RepairReasonInput');

export const GuestAccessSchema = z
  .object({
    email: EmailSchema,
    accessCode: OpaqueIdentifierSchema,
  })
  .strict()
  .openapi('GuestAccess');

export const CreateRepairRequestSchema = z
  .object({
    locale: LocaleSchema,
    shippingAddress: ServiceAddressSchema,
    billingAddress: ServiceAddressSchema,
    deliveryOption: DeliveryOptionSchema,
    dropoffBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    modeOfTransport: ModeOfTransportSchema.optional(),
    pickupBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    repairItem: RepairItemSchema,
    requestReason: RepairReasonInputSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.shippingAddress.email !== value.billingAddress.email) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['billingAddress', 'email'],
        message: 'Shipping and billing email must match',
      });
    }
  })
  .openapi('CreateRepairRequest');

export const EcsCreateRepairRequestSchema = z
  .object({
    brand: EcsBrandSchema,
    language: z.string().min(2).max(32),
    shippingAddress: ServiceAddressSchema,
    billingAddress: ServiceAddressSchema,
    deliveryOption: DeliveryOptionSchema,
    dropoffBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    modeOfTransport: ModeOfTransportSchema.optional(),
    pickupBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    repairItem: RepairItemSchema,
    requestReason: RepairReasonInputSchema,
  })
  .strict()
  .openapi('EcsCreateRepairRequest');

const MutableRepairItemSchema = RepairItemSchema.partial();

export const UpdateRepairRequestSchema = z
  .object({
    repairNumber: OpaqueIdentifierSchema,
    shippingAddress: ServiceAddressSchema.optional(),
    billingAddress: ServiceAddressSchema.optional(),
    deliveryOption: DeliveryOptionSchema.optional(),
    modeOfTransport: ModeOfTransportSchema.optional(),
    pickupBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    repairItem: MutableRepairItemSchema.optional(),
    requestReason: RepairReasonInputSchema.optional(),
    guestAccess: GuestAccessSchema.optional(),
  })
  .strict()
  .refine(
    ({ repairNumber: _repairNumber, guestAccess: _guestAccess, ...fields }) =>
      Object.values(fields).some((value) => value !== undefined),
    'At least one mutable repair field is required'
  )
  .openapi('UpdateRepairRequest');

export const EcsUpdateRepairRequestSchema = z
  .object({
    repairNumber: OpaqueIdentifierSchema,
    shippingAddress: ServiceAddressSchema.optional(),
    billingAddress: ServiceAddressSchema.optional(),
    deliveryOption: DeliveryOptionSchema.optional(),
    modeOfTransport: ModeOfTransportSchema.optional(),
    pickupBoutiqueTracking: OpaqueIdentifierSchema.optional(),
    repairItem: MutableRepairItemSchema.optional(),
    requestReason: RepairReasonInputSchema.optional(),
  })
  .strict();

export const RepairQuerySchema = z
  .object({
    countryCode: CountryCodeSchema,
    page: OptionalPageQuerySchema,
    repairNumber: OpaqueIdentifierSchema.optional(),
    watchSerial: OpaqueIdentifierSchema.optional(),
  })
  .strict()
  .openapi('RepairQuery');

export const GuestRepairQuerySchema = z
  .object({
    countryCode: CountryCodeSchema,
    email: EmailSchema,
    accessCode: OpaqueIdentifierSchema,
    page: OptionalPageQuerySchema,
  })
  .strict()
  .openapi('GuestRepairQuery');

export const RepairReasonQuerySchema = z
  .object({ countryCode: CountryCodeSchema, locale: LocaleSchema })
  .strict()
  .openapi('RepairReasonQuery');

export const RecommendPriceQuerySchema = z
  .object({
    countryCode: CountryCodeSchema,
    locale: LocaleSchema,
    // An empty reference is valid while the customer is browsing the catalogue.
    watchReference: z
      .string()
      .trim()
      .max(128)
      .refine((value) => !containsControlCharacters(value), 'Must not contain control characters'),
  })
  .strict()
  .openapi('RecommendPriceQuery');

export const WatchDetailsQuerySchema = z
  .object({
    countryCode: CountryCodeSchema,
    locale: LocaleSchema,
    watchReference: OpaqueIdentifierSchema,
  })
  .strict()
  .openapi('WatchDetailsQuery');

export const WatchProductSummarySchema = z
  .object({
    id: z.string().min(1),
    sku: z.string().min(1),
    name: z.string(),
    slug: z.string().optional(),
    thumbnailUrl: z.string().url().nullable().optional(),
    mediaLargeUrl: z.string().url().nullable().optional(),
    warrantyExtensionDuration: z.string().min(1).optional(),
  })
  .strict()
  .openapi('WatchProductSummary');

export const CheckEmailRequestSchema = z
  .object({ email: EmailSchema })
  .strict()
  .openapi('ServiceCheckEmailRequest');

export const CheckEmailResponseSchema = z
  .object({ exists: z.boolean() })
  .strict()
  .openapi('ServiceCheckEmailResponse');

export const TrackingQuerySchema = z
  .object({ countryCode: CountryCodeSchema, email: EmailSchema })
  .strict()
  .openapi('TrackingQuery');

export const TransactionDataSchema = z
  .object({
    amount: z.number().finite().nonnegative(),
    currency: z
      .string()
      .trim()
      .length(3)
      .regex(/^[A-Za-z]{3}$/)
      .transform((value) => value.toUpperCase()),
    paymentMethod: z.enum([
      'card',
      'scheme',
      'paypal',
      'applepay',
      'googlepay',
      'klarna_paynow',
      'affirm',
      'zip',
      'twint',
      'v12',
      'cembra_installment',
      'cembra_invoice',
      'creditplus',
      'agos',
      'orico_financing',
      'smbc',
      'offline_banktransfer',
    ]),
    transactionId: OpaqueIdentifierSchema,
  })
  .strict()
  .openapi('RepairTransactionData');

export const RefuseQuoteRequestSchema = z
  .object({
    repairNumber: OpaqueIdentifierSchema,
    quoteId: OpaqueIdentifierSchema,
    refusalReason: OpaqueIdentifierSchema,
    transactionData: TransactionDataSchema.optional(),
  })
  .strict()
  .openapi('RefuseQuoteRequest');

export const EcsRefuseQuoteRequestSchema = z
  .object({
    repairNumber: OpaqueIdentifierSchema,
    quoteId: OpaqueIdentifierSchema,
    refusalReason: OpaqueIdentifierSchema,
    transactionData: TransactionDataSchema.optional(),
  })
  .strict()
  .openapi('EcsRefuseQuoteRequest');

export const PaymentSelectionRequestSchema = z
  .object({
    locale: LocaleSchema,
    repairNumber: OpaqueIdentifierSchema,
    quoteId: OpaqueIdentifierSchema,
    selectedServiceIds: z.array(OpaqueIdentifierSchema).max(100),
    guestAccess: GuestAccessSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.selectedServiceIds).size !== value.selectedServiceIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['selectedServiceIds'],
        message: 'Duplicate service IDs are not allowed',
      });
    }
  })
  .openapi('PaymentSelectionRequest');

const EcsAcceptedServiceSchema = z
  .object({ id: OpaqueIdentifierSchema, accepted: z.literal(true) })
  .strict();
const EcsAcceptQuoteSchema = z
  .object({
    repairItems: z.array(
      z
        .object({ itemId: OpaqueIdentifierSchema, services: z.array(EcsAcceptedServiceSchema) })
        .strict()
    ),
    variousServices: z.array(EcsAcceptedServiceSchema),
  })
  .strict();

const EcsRepairPaymentRequestSchema = z
  .object({
    accept_quote: EcsAcceptQuoteSchema,
    amount: z.number().finite().nonnegative(),
    channel: OpaqueIdentifierSchema,
    country_code: CountryCodeSchema,
    currency: z.string().length(3),
    locale: z.string().min(2).max(32),
    return_url: z.string().url(),
    repairNumber: OpaqueIdentifierSchema,
    quoteId: OpaqueIdentifierSchema,
    customer_email: EmailSchema.optional(),
    brand: EcsBrandSchema.optional(),
  })
  .strict();

export const EcsPaymentMethodsRequestSchema = EcsRepairPaymentRequestSchema.openapi(
  'EcsPaymentMethodsRequest'
);

export const EcsPaymentSessionRequestSchema = EcsRepairPaymentRequestSchema.openapi(
  'EcsPaymentSessionRequest'
);

export const PaymentMethodSchema = z
  .object({
    type: OpaqueIdentifierSchema,
    brands: z.array(z.string()).default([]),
    configuration: z.record(z.string(), z.unknown()).default({}),
    name: z.string().optional(),
  })
  .strict()
  .openapi('ServicePaymentMethod');

export const EcsPaymentMethodsResponseSchema = z
  .object({
    code: z.number().int().optional(),
    msg: z.string().optional(),
    data: z
      .object({ paymentMethods: z.array(PaymentMethodSchema) })
      .strict()
      .optional(),
    paymentMethods: z.array(PaymentMethodSchema).optional(),
  })
  .strict()
  .refine(
    (value) => Boolean(value.paymentMethods || value.data?.paymentMethods),
    'Payment methods are missing'
  )
  .openapi('EcsPaymentMethodsResponse');

const EcsPaymentSessionPayloadBaseSchema = z
  .object({
    adyen_payment_id: OpaqueIdentifierSchema.optional(),
    amount: z.union([z.string(), z.number()]).optional(),
    channel: OpaqueIdentifierSchema.optional(),
    country_code: CountryCodeSchema.optional(),
    currency: z.string().length(3).optional(),
    customer_email: EmailSchema.optional(),
    expires_at: z.string().optional(),
    locale: z.string().optional(),
    payment_id: OpaqueIdentifierSchema.optional(),
    payment_required: z.boolean().optional(),
    quoteId: OpaqueIdentifierSchema.optional(),
    repairNumber: OpaqueIdentifierSchema.optional(),
    return_url: z.string().url().optional(),
    session_data: z.string().optional(),
    id: OpaqueIdentifierSchema.optional(),
    sessionData: z.string().optional(),
  })
  .strict();

function refinePayablePaymentSessionPayload(
  value: z.infer<typeof EcsPaymentSessionPayloadBaseSchema>,
  ctx: z.RefinementCtx
): void {
  if (value.payment_required === false) return;
  if (!value.session_data && !value.sessionData) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Payment session data is missing' });
  }
  if (!value.id && !value.adyen_payment_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Adyen payment session ID is missing' });
  }
}

const EcsPaymentSessionPayloadSchema = EcsPaymentSessionPayloadBaseSchema.superRefine(
  refinePayablePaymentSessionPayload
);

export const EcsAppPaymentSessionPayloadSchema = EcsPaymentSessionPayloadBaseSchema.extend({
  accept_quote: EcsAcceptQuoteSchema.optional(),
  brand: EcsBrandSchema.optional(),
}).superRefine(refinePayablePaymentSessionPayload);

export const EcsPaymentSessionResponseSchema = z
  .union([
    EcsPaymentSessionPayloadSchema,
    z
      .object({
        code: z.number().int().optional(),
        msg: z.string().optional(),
        data: EcsPaymentSessionPayloadSchema,
      })
      .strict(),
  ])
  .openapi('EcsPaymentSessionResponse');

export const EcsAppPaymentSessionResponseSchema = z
  .union([
    EcsAppPaymentSessionPayloadSchema,
    z
      .object({
        code: z.number().int().optional(),
        msg: z.string().optional(),
        data: EcsAppPaymentSessionPayloadSchema,
      })
      .strict(),
  ])
  .openapi('EcsAppPaymentSessionResponse');

export const RepairPageSchema = z
  .object({
    isFirst: z.boolean(),
    isLast: z.boolean(),
    page: z.number().int(),
    perPage: z.number().int(),
    title: z.string(),
    total: z.number().int(),
    totalPage: z.number().int(),
  })
  .strict();

export const RepairReasonItemSchema = z.object({
  code: OpaqueIdentifierSchema,
  title: z.string(),
});
export const RepairReasonResponseSchema = z
  .object({
    items: z.array(RepairReasonItemSchema).optional(),
  })
  .strict()
  .openapi('RepairReasonResponse');

export const RecommendPriceSchema = z
  .object({
    category1: z.string(),
    category2: z.string(),
    currency: z.string().length(3),
    description: z.string(),
    price: z.number().finite().nullable(),
    title: z.string(),
  })
  .strict();
export const RecommendPriceResponseSchema = z
  .object({
    model: z.string().nullable().optional(),
    packShipIncluded: z.number().int().optional(),
    prices: z.array(RecommendPriceSchema).optional(),
    ref: z.string().nullable().optional(),
    vatIncluded: z.number().int().optional(),
  })
  .strict()
  .openapi('RecommendPriceResponse');

export const ServiceStatusSchema = z.enum([
  'request_created',
  'repair_box_request',
  'box_sent',
  'request_transit',
  'watch_received',
  'analysis',
  'estimate_sent',
  'estimate_refused',
  'estimate_accepted',
  'preinvoice_done',
  'workshop',
  'waiting_payment',
  'payment_done',
  'payment_refused',
  'shipping_todo',
  'shipping_done',
  'shipping_done_boutique',
  'pickup_ready',
  'completed',
]);

export const RepairServiceSchema = z
  .object({
    accepted: z.boolean(),
    currency: z.string().length(3).optional(),
    description: z.string().optional(),
    discountType: z.string().optional(),
    discountValue: z.number().optional(),
    finalPrice: z.number().finite().optional(),
    id: OpaqueIdentifierSchema,
    optional: z.boolean(),
    originalPrice: z.number().finite().optional(),
    taxes: z.number().finite().optional(),
    title: z.string(),
  })
  .strict();

export const RepairDetailsSchema = z
  .object({
    actionsHistory: z.array(
      z
        .object({
          actionCode: ServiceStatusSchema,
          dateTime: z.string().optional(),
          quoteId: OpaqueIdentifierSchema.optional(),
          status: z.enum(['In progress', 'Completed']).optional(),
        })
        .strict()
    ),
    billingAddress: ServiceAddressSchema,
    boutiqueName: z.string(),
    boutiqueTracking: z.string(),
    creationDateTime: z.string(),
    deliveryOption: DeliveryOptionSchema,
    deliveryOptionEditable: z.boolean(),
    dropoffBoutiqueName: z.string().optional(),
    dropoffBoutiqueTracking: z.string().optional(),
    modeOfTransport: ModeOfTransportSchema,
    pickupBoutiqueName: z.string().optional(),
    pickupBoutiqueTracking: z.string().optional(),
    plannedDeliveryDate: z.string(),
    quoteId: OpaqueIdentifierSchema.optional(),
    repairItems: z.array(
      z
        .object({
          dateOfPurchase: z.string(),
          name: z.string(),
          reasonCodes: z.array(z.string()),
          reference: z.string(),
          remarks: z.string(),
          serial: z.string(),
          type: z.string(),
          underWarranty: z.boolean(),
        })
        .strict()
    ),
    repairNumber: OpaqueIdentifierSchema,
    shippingAddress: ServiceAddressSchema,
  })
  .strict();

export const RepairListResponseSchema = RepairPageSchema.extend({
  items: z.array(RepairDetailsSchema).optional(),
})
  .strict()
  .openapi('RepairListResponse');

/** ECS sends null for values it does not have. Drop those before parse so absence passes and a present value still has to match. */
function dropEcsNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.filter((entry) => entry !== null).map(dropEcsNulls);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, entry]) =>
        entry === null ? [] : [[key, dropEcsNulls(entry)]]
      )
    );
  }
  return value;
}

const QuoteDetailObjectSchema = z
  .object({
    country: CountryCodeSchema,
    deadline: z.string().optional(),
    quoteCreationDateTime: z.string().optional(),
    remarks: z.string().optional(),
    repairItems: z
      .array(
        z
          .object({
            itemId: OpaqueIdentifierSchema,
            name: z.string().optional(),
            reasonCodes: z.array(z.string()).optional(),
            reference: z.string().optional(),
            serial: z.string().optional(),
            services: z.array(RepairServiceSchema),
            type: z.enum(['Strap', 'Buckle', 'Watch', 'Bracelet', 'Utc', 'Other']),
            underWarranty: z.boolean().optional(),
            diagnoObservations: z
              .array(z.object({ comments: z.string().optional(), title: z.string() }).strict())
              .optional(),
          })
          .strict()
      )
      .optional(),
    quoteId: OpaqueIdentifierSchema.optional(),
    repairNumber: OpaqueIdentifierSchema,
    variousServices: z.array(RepairServiceSchema).optional(),
    waitingConfirmation: z.boolean(),
  })
  .strict()
  .openapi('RepairQuote');

export const QuoteDetailSchema = z.preprocess(dropEcsNulls, QuoteDetailObjectSchema);

export const RepairReferenceResponseSchema = z
  .object({ repairNumber: OpaqueIdentifierSchema })
  .openapi('RepairReferenceResponse');
export const AcknowledgedResponseSchema = z
  .object({ acknowledged: z.literal(true), alreadyRefused: z.boolean().optional() })
  .strict()
  .openapi('ServiceAcknowledgedResponse');
export const PaymentMethodsResponseSchema = z
  .object({ paymentMethods: z.array(PaymentMethodSchema) })
  .strict()
  .openapi('ServicePaymentMethodsResponse');
export const PaymentSessionResponseSchema = z
  .discriminatedUnion('paymentRequired', [
    z.object({ paymentRequired: z.literal(false) }).strict(),
    z
      .object({
        paymentRequired: z.literal(true),
        id: OpaqueIdentifierSchema,
        sessionData: z.string(),
      })
      .strict(),
  ])
  .openapi('ServicePaymentSessionResponse');

export type CreateRepairRequest = z.infer<typeof CreateRepairRequestSchema>;
export type EcsCreateRepairRequest = z.infer<typeof EcsCreateRepairRequestSchema>;
export type UpdateRepairRequest = z.infer<typeof UpdateRepairRequestSchema>;
export type EcsUpdateRepairRequest = z.infer<typeof EcsUpdateRepairRequestSchema>;
export type RepairQuery = z.infer<typeof RepairQuerySchema>;
export type GuestRepairQuery = z.infer<typeof GuestRepairQuerySchema>;
export type RepairReasonQuery = z.infer<typeof RepairReasonQuerySchema>;
export type RecommendPriceQuery = z.infer<typeof RecommendPriceQuerySchema>;
export type WatchDetailsQuery = z.infer<typeof WatchDetailsQuerySchema>;
export type WatchProductSummary = z.infer<typeof WatchProductSummarySchema>;
export type CheckEmailRequest = z.infer<typeof CheckEmailRequestSchema>;
export type CheckEmailResponse = z.infer<typeof CheckEmailResponseSchema>;
export type TrackingQuery = z.infer<typeof TrackingQuerySchema>;
export type RefuseQuoteRequest = z.infer<typeof RefuseQuoteRequestSchema>;
export type EcsRefuseQuoteRequest = z.infer<typeof EcsRefuseQuoteRequestSchema>;
export type PaymentSelectionRequest = z.infer<typeof PaymentSelectionRequestSchema>;
export type EcsPaymentMethodsRequest = z.infer<typeof EcsPaymentMethodsRequestSchema>;
export type EcsPaymentSessionRequest = z.infer<typeof EcsPaymentSessionRequestSchema>;
export type RepairDetails = z.infer<typeof RepairDetailsSchema>;
export type RepairListResponse = z.infer<typeof RepairListResponseSchema>;
export type RepairReasonResponse = z.infer<typeof RepairReasonResponseSchema>;
export type RecommendPriceResponse = z.infer<typeof RecommendPriceResponseSchema>;
export type RepairService = z.infer<typeof RepairServiceSchema>;
export type QuoteDetail = z.infer<typeof QuoteDetailSchema>;
export type RepairReferenceResponse = z.infer<typeof RepairReferenceResponseSchema>;
export type AcknowledgedResponse = z.infer<typeof AcknowledgedResponseSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type PaymentMethodsResponse = z.infer<typeof PaymentMethodsResponseSchema>;
export type PaymentSessionResponse = z.infer<typeof PaymentSessionResponseSchema>;
